import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

import bus, { eventNames } from '@/lib/eventBus'
import featureNames from '@/lib/featureNames'
import { useAiAssistantStore } from '@/stores/aiAssistant'

import { useOnboardingFeatures } from './useOnboardingFeatures'

type Translate = (key: string, ...args: unknown[]) => string

export function useOnboardingWizard(options: {
  t: Translate
  hasMissingOnboardingData: () => boolean
  getMissingOnboardingDataSummary: () => string
  /** Called when continue is blocked by missing data (e.g. jump to the incomplete form step). */
  onMissingOnboardingData?: () => void
  /** Required legal consents (privacy, guideline, AGB, data confirmation) not all ticked. */
  hasMissingLegalConsents?: () => boolean
  /** Called when the manual form's continue is blocked by unticked required consents. */
  onMissingLegalConsents?: () => void
}) {
  const {
    t,
    hasMissingOnboardingData,
    getMissingOnboardingDataSummary,
    onMissingOnboardingData,
    hasMissingLegalConsents,
    onMissingLegalConsents,
  } = options
  const { isFeatureActive, getFeature, isAssistedOnboardingEnabled } = useOnboardingFeatures()
  const wizardStep = ref(1)
  const { manualOnboardingActive } = storeToRefs(useAiAssistantStore())

  const assistantChatUnlocked = computed(
    () => isAssistedOnboardingEnabled.value && !manualOnboardingActive.value
  )
  // Assisted Step 2 is chat-only: all input goes through the assistant. The side
  // form (OnboardingDataEntry) is shown only in the manual/form fallback — there
  // is no split chat+form layout.
  const activeEnterDataMode = computed<'chat' | 'form'>(() =>
    assistantChatUnlocked.value ? 'chat' : 'form'
  )
  const showAssistantChat = assistantChatUnlocked
  const showFormColumn = computed(() => !assistantChatUnlocked.value)
  const hasBillingStep = computed(() => isFeatureActive(featureNames.CONTRACT))
  const hasGuideStep = computed(
    () =>
      isFeatureActive(featureNames.LANDINGPAGE) &&
      !!getFeature(featureNames.LANDINGPAGE)?.config?.canBookLandingpages
  )
  const billingWizardStep = computed(() => (hasBillingStep.value ? 3 : null))
  const guideWizardStep = computed(() => {
    if (!hasGuideStep.value) return null
    return hasBillingStep.value ? 4 : 3
  })
  const reviewWizardStep = computed(() => {
    let step = 3
    if (hasBillingStep.value) step += 1
    if (hasGuideStep.value) step += 1
    return step
  })
  const stepBeforeReview = computed(() => {
    if (hasGuideStep.value) return guideWizardStep.value
    if (hasBillingStep.value) return billingWizardStep.value
    return 2
  })
  const continueFromEnterDataLabel = computed(() => {
    if (hasBillingStep.value) return t('onboarding.wizard.data-entry.continue-to-billing')
    if (hasGuideStep.value) return t('onboarding.wizard.data-entry.continue-to-guides')
    return t('onboarding.wizard.data-entry.continue-to-review')
  })
  const wizardStepSlug = computed(() => {
    if (wizardStep.value === 1) return 'basic-info'
    if (wizardStep.value === 2) return 'enter-data'
    if (hasBillingStep.value && wizardStep.value === billingWizardStep.value) return 'payment'
    if (hasGuideStep.value && wizardStep.value === guideWizardStep.value) return 'guide'
    return 'review'
  })

  function stepFromSlug(slug: string): number {
    const map: Record<string, number> = {
      welcome: 1,
      'basic-info': 1,
      company: 1,
      'enter-data': 2,
      publication: 2,
      payment: billingWizardStep.value ?? 3,
      guide: guideWizardStep.value ?? reviewWizardStep.value,
      review: reviewWizardStep.value,
      validation: reviewWizardStep.value,
    }
    return map[slug] ?? 1
  }

  function handleContinueToReview() {
    if (hasMissingOnboardingData()) {
      onMissingOnboardingData?.()
      // In assisted mode the user has no form in front of them, so a warning
      // alone leaves them stuck. Drop them into the manual form where the
      // missing fields are visible and editable. In form mode the missing
      // fields are already listed inline below the form.
      if (assistantChatUnlocked.value) {
        useAiAssistantStore().switchToManualOnboarding()
        bus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'warning',
          text: t('onboarding.wizard.review.missing-fields-hint', {
            fields: getMissingOnboardingDataSummary(),
          }),
        })
      }
      return
    }
    // The manual form shows the consents on its last step ("Connections & legal"),
    // so stop there instead of letting the user reach a review they can't complete.
    // Assisted mode collects them in chat and on the review step itself.
    if (!assistantChatUnlocked.value && hasMissingLegalConsents?.()) {
      onMissingLegalConsents?.()
      return
    }
    const active = document.activeElement
    if (active instanceof HTMLElement) active.blur()
    useAiAssistantStore().clearRequestedFieldFocus()
    if (hasBillingStep.value) wizardStep.value = billingWizardStep.value as number
    else if (hasGuideStep.value) wizardStep.value = guideWizardStep.value as number
    else wizardStep.value = reviewWizardStep.value
  }

  function handleBillingStepNav(delta: number) {
    if (delta === -1) {
      wizardStep.value = 2
      return
    }
    if (delta === 1) {
      wizardStep.value = hasGuideStep.value
        ? (guideWizardStep.value as number)
        : reviewWizardStep.value
    }
  }

  function handleGuideStepNav(delta: number) {
    if (delta === -1) {
      wizardStep.value = hasBillingStep.value ? (billingWizardStep.value as number) : 2
      return
    }
    if (delta === 1) wizardStep.value = reviewWizardStep.value
  }

  return {
    wizardStep,
    manualOnboardingActive,
    isAssistedOnboardingEnabled,
    activeEnterDataMode,
    showAssistantChat,
    showFormColumn,
    hasBillingStep,
    hasGuideStep,
    billingWizardStep,
    guideWizardStep,
    reviewWizardStep,
    stepBeforeReview,
    continueFromEnterDataLabel,
    wizardStepSlug,
    stepFromSlug,
    handleContinueToReview,
    handleBillingStepNav,
    handleGuideStepNav,
  }
}
